import type { PermissionsMap } from "@printflow/auth-shared";

/**
 * Cliente HTTP del worker.
 *
 * `credentials: "include"` es OBLIGATORIO en toda llamada: la sesion viaja por
 * cookie, no por header Bearer. Sin esto el worker responde 401 aunque el
 * usuario este logueado — y el sintoma es indistinguible de credenciales malas.
 *
 * `X-Requested-With` lo exige la defensa CSRF del worker en las mutaciones.
 */

/** Base del worker. En local, 9987 (ver wrangler.toml). */
export const API_BASE: string = import.meta.env.VITE_API_BASE ?? "http://localhost:9987";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...init?.headers,
    },
  });

  if (!res.ok) {
    // El worker responde `{ error: string }` en los paths de error. Si el
    // cuerpo no es JSON (502 de la red, HTML de Cloudflare), no explotamos:
    // devolvemos el status, que ya es diagnostico suficiente.
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // cuerpo no-JSON: nos quedamos con el status.
    }
    throw new ApiError(res.status, message);
  }

  // Un 204 no trae cuerpo: `res.json()` lanzaría sobre una mutación que salió bien.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * GET tipado.
 *
 * `init` existe por el `signal`: la consulta de tarifa del presupuesto se
 * cancela mientras se tipea, y una respuesta vieja que llega tarde pisaría el
 * precio de la cantidad actual.
 */
export function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, init);
}

/**
 * Mutacion tipada.
 *
 * `X-Requested-With` ya lo pone `request`; lo que agrega esto es serializar el
 * cuerpo en un solo lugar, para que ninguna pantalla se olvide del
 * `JSON.stringify` y mande `[object Object]`.
 */
export function apiMutar<T>(
  metodo: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  cuerpo?: unknown,
): Promise<T> {
  return request<T>(path, {
    method: metodo,
    ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
  });
}

/**
 * Sube un archivo por `multipart/form-data`.
 *
 * No pasa por `request` porque **el `Content-Type` lo tiene que poner el
 * browser**: multipart lleva un `boundary` generado, y fijarlo a
 * `application/json` como hace `request` rompe el parseo del lado del worker.
 */
export async function apiSubir<T>(
  path: string,
  form: FormData,
  metodo: "POST" | "PATCH" = "POST",
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: metodo,
    body: form,
    credentials: "include",
    headers: { "X-Requested-With": "XMLHttpRequest" },
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // cuerpo no-JSON: nos quedamos con el status.
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

/**
 * Descarga un archivo protegido como blob.
 *
 * **La URL no se puede usar directo en un `<img src>` ni en un `<a href>`**: el
 * worker sirve todo con `Content-Disposition: attachment` a proposito
 * (ADR 0004), para que un PDF con `/JavaScript` no ejecute nunca en el origen de
 * la app. Traerlo con `fetch` —que si manda la cookie— y armar un
 * `URL.createObjectURL` deja mostrar la miniatura en un `<img>`, que solo
 * interpreta imagenes, sin agregar un endpoint que sirva `inline` y reabra ese
 * vector.
 *
 * Quien lo llama es responsable de `URL.revokeObjectURL` cuando termina.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "X-Requested-With": "XMLHttpRequest" },
  });
  if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
  return await res.blob();
}

/** Identidad + capacidades del usuario logueado. */
export interface Me {
  id: string;
  email: string;
  nombre: string;
  telefono: string | null;
  direccion: string | null;
  roles: string[];
  /** Mapa `recurso -> acciones`. Es lo que consume `can()`. */
  permisos: PermissionsMap;
  /** Los mismos permisos aplanados a codigos `recurso.accion`. */
  codigos: string[];
}

export function fetchMe(): Promise<Me> {
  return request<Me>("/api/v1/me");
}
